/**
 * Proje İlerleme — Faz 30 (Work Item Aylık İlerleme)
 * =====================================================
 * Ay sekmeleri + Work Item satırları + Planlı/Gerçek tarih + %5 adımlı slider
 * + WBS/Lokasyon filtreleri + Log dialog. Ayda "Yeni Ay Ekle" ile önceki ayın
 * bitmemiş kalemleri sonraki aya carry-over olur.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Package, Plus, Clock, ChartLine, FloppyDisk,
} from "@phosphor-icons/react";

const fmt = (n, dec = 1) =>
  n == null || Number.isNaN(Number(n))
    ? "-"
    : Number(n).toLocaleString("tr-TR",
        { minimumFractionDigits: dec, maximumFractionDigits: dec });

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran",
                 "Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
  return `${names[Number(m) - 1] || m} ${y}`;
};

const currentYm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function ProjectProgress() {
  const { siteId } = useParams();
  const [site, setSite] = useState(null);
  const [months, setMonths] = useState([]);
  const [activeMonth, setActiveMonth] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [dirty, setDirty] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [newYm, setNewYm] = useState(currentYm());
  const [logsFor, setLogsFor] = useState(null); // {work_item_id, name}
  const [logs, setLogs] = useState([]);
  const [hkMap, setHkMap] = useState({});   // work_item_id → [contract lines]
  // Filtreler
  const [fWbs, setFWbs] = useState("all");
  const [fLoc, setFLoc] = useState("all");
  const [fSearch, setFSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, siteR, hk] = await Promise.all([
        axios.get(`${API}/work-item-progress/${siteId}`),
        axios.get(`${API}/sites/${siteId}/project`),
        axios.get(`${API}/work-item-progress/hakedis-map/${siteId}`),
      ]);
      const mm = pr.data.months || [];
      setMonths(mm);
      setActiveMonth((prev) => (prev && mm.includes(prev)) ? prev : (mm[mm.length - 1] || ""));
      setRows(pr.data.items || []);
      setSite({ id: siteId, name: siteR.data.site_name, code: siteR.data.site_code });
      setHkMap(hk.data.map || {});
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let out = rows.filter((r) => r.year_month === activeMonth);
    if (fWbs !== "all") out = out.filter((r) => r.wbs_node_id === fWbs);
    if (fLoc !== "all") {
      if (fLoc === "__root__") out = out.filter((r) => !r.location_id);
      else out = out.filter((r) => r.location_id === fLoc);
    }
    if (fSearch.trim()) {
      const q = fSearch.trim().toLocaleLowerCase("tr-TR");
      out = out.filter((r) =>
        (r.name || "").toLocaleLowerCase("tr-TR").includes(q) ||
        (r.wbs_path || "").toLocaleLowerCase("tr-TR").includes(q) ||
        (r.location_label || "").toLocaleLowerCase("tr-TR").includes(q));
    }
    return out;
  }, [rows, activeMonth, fWbs, fLoc, fSearch]);

  const wbsOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) {
      if (r.wbs_node_id && !seen.has(r.wbs_node_id))
        seen.set(r.wbs_node_id, r.wbs_path || r.name);
    }
    return Array.from(seen.entries());
  }, [rows]);
  const locOptions = useMemo(() => {
    const seen = new Map();
    let hasRoot = false;
    for (const r of rows) {
      if (!r.location_id) { hasRoot = true; continue; }
      if (!seen.has(r.location_id)) seen.set(r.location_id, r.location_label);
    }
    return { list: Array.from(seen.entries()), hasRoot };
  }, [rows]);

  const totals = useMemo(() => {
    const n = filtered.length;
    const sum = filtered.reduce((a, r) => a + Number(r.progress_percent || 0), 0);
    const done = filtered.filter((r) => Number(r.progress_percent || 0) >= 100).length;
    const started = filtered.filter((r) => Number(r.progress_percent || 0) > 0 && Number(r.progress_percent || 0) < 100).length;
    return { n, avg: n ? sum / n : 0, done, started };
  }, [filtered]);

  const updateLocal = (id, patch) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    setDirty((d) => ({ ...d, [id]: true }));
  };

  const save = async (row) => {
    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      const { data } = await axios.put(`${API}/work-item-progress/${row.id}`, {
        work_item_id: row.work_item_id,
        year_month: row.year_month,
        planned_start: row.planned_start || "",
        planned_finish: row.planned_finish || "",
        actual_start: row.actual_start || "",
        actual_finish: row.actual_finish || "",
        progress_percent: Number(row.progress_percent || 0),
        note: row.note || "",
      });
      // Snap to backend response
      updateLocal(row.id, { progress_percent: data.progress_percent, prev_progress: data.prev_progress });
      setDirty((d) => { const nx = { ...d }; delete nx[row.id]; return nx; });
      toast.success("Kaydedildi");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving((s) => { const nx = { ...s }; delete nx[row.id]; return nx; }); }
  };

  const addMonth = async () => {
    if (!/^\d{4}-\d{2}$/.test(newYm)) {
      toast.error("Yıl-ay YYYY-AA formatında olmalı"); return;
    }
    try {
      const { data } = await axios.post(`${API}/work-item-progress/add-month`, {
        site_id: siteId, year_month: newYm,
      });
      toast.success(`Ay eklendi: ${data.created} kayıt (atlandı: ${data.skipped})`);
      setAddOpen(false);
      setActiveMonth(newYm);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openLogs = async (row) => {
    setLogsFor({ work_item_id: row.work_item_id, name: row.name });
    setLogs([]);
    try {
      const { data } = await axios.get(`${API}/work-item-progress/logs/${row.work_item_id}`);
      setLogs(data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Shell
      title={site ? `${site.name} — Proje İlerleme` : "Yükleniyor…"}
      subtitle="Proje Yönetimi › Aylık Work Item İlerleme"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="progress-add-month-btn" onClick={() => setAddOpen(true)}
                  className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea01a] text-white h-9 text-xs uppercase tracking-widest">
            <Plus size={14} className="mr-1.5" /> Yeni Ay Ekle
          </Button>
          <Link to={`/proje/${siteId}/work-items`}>
            <Button variant="outline" data-testid="progress-go-work-items"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <Package size={14} className="mr-1.5" /> Work Items
            </Button>
          </Link>
          <Link to={`/proje/${siteId}/gantt`}>
            <Button variant="outline" data-testid="progress-go-gantt"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ChartLine size={14} className="mr-1.5" /> Gantt
            </Button>
          </Link>
          <Link to="/proje/ilerleme">
            <Button variant="ghost" className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ArrowLeft size={14} className="mr-1.5" /> Liste
            </Button>
          </Link>
        </div>
      }
    >
      {/* Month tabs */}
      {months.length > 0 ? (
        <div className="mb-3 flex items-center gap-1 flex-wrap" data-testid="progress-month-tabs">
          {months.map((m) => (
            <button key={m}
                    onClick={() => setActiveMonth(m)}
                    data-testid={`progress-month-tab-${m}`}
                    className={`px-3 h-9 text-[11px] uppercase tracking-widest font-bold rounded-none sm:rounded-sm border ${
                      activeMonth === m
                        ? "bg-[#1B4776] text-white border-[#1B4776]"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}>
              {monthLabel(m)}
            </button>
          ))}
        </div>
      ) : (
        <Card className="p-8 rounded-none sm:rounded-sm text-center mb-4 border-dashed border-2 border-slate-200">
          <div className="text-sm font-bold text-slate-700 mb-1">Henüz ay eklenmemiş</div>
          <div className="text-xs text-slate-500 mb-3">Work Item ilerlemelerini takip etmek için ilk ayı ekleyin.</div>
          <Button onClick={() => setAddOpen(true)} data-testid="progress-add-first-month"
                  className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea01a] text-white text-xs uppercase tracking-widest">
            <Plus size={14} className="mr-1.5" /> Yeni Ay Ekle
          </Button>
        </Card>
      )}

      {/* KPI */}
      {activeMonth && (
        <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 rounded-none sm:rounded-sm border-l-4 border-[#1B4776]">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Work Item</div>
            <div className="text-2xl font-bold text-[#1B4776] font-heading">{totals.n}</div>
          </Card>
          <Card className="p-3 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Ort. İlerleme</div>
            <div className="text-2xl font-bold text-[#556B10] font-heading">%{fmt(totals.avg, 1)}</div>
          </Card>
          <Card className="p-3 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Devam Eden</div>
            <div className="text-2xl font-bold text-amber-600 font-heading">{totals.started}</div>
          </Card>
          <Card className="p-3 rounded-none sm:rounded-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tamamlanan</div>
            <div className="text-2xl font-bold text-emerald-600 font-heading">{totals.done}</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      {activeMonth && (
        <Card className="p-3 rounded-none sm:rounded-sm mb-3 flex flex-wrap items-center gap-2">
          <Input
            data-testid="progress-search"
            placeholder="Ara: iş kalemi, WBS, lokasyon..."
            value={fSearch}
            onChange={(e) => setFSearch(e.target.value)}
            className="h-9 max-w-xs rounded-none sm:rounded-sm text-xs" />
          <Select value={fWbs} onValueChange={setFWbs}>
            <SelectTrigger data-testid="progress-filter-wbs" className="h-9 w-64 rounded-none sm:rounded-sm text-xs">
              <SelectValue placeholder="WBS filtresi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm WBS</SelectItem>
              {wbsOptions.map(([id, label]) => (
                <SelectItem key={id} value={id}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fLoc} onValueChange={setFLoc}>
            <SelectTrigger data-testid="progress-filter-loc" className="h-9 w-64 rounded-none sm:rounded-sm text-xs">
              <SelectValue placeholder="Lokasyon filtresi" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Lokasyonlar</SelectItem>
              {locOptions.hasRoot && <SelectItem value="__root__">— (Proje Kökü)</SelectItem>}
              {locOptions.list.map(([id, label]) => (
                <SelectItem key={id} value={id}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <div className="text-[11px] text-slate-500 font-mono">{filtered.length} / {rows.filter(r => r.year_month === activeMonth).length}</div>
        </Card>
      )}

      {/* Rows table */}
      {activeMonth && (
        <Card className="rounded-none sm:rounded-sm overflow-hidden" data-testid="progress-rows-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1B4776] text-white text-[10px] uppercase tracking-widest">
                  <th className="text-left px-2 py-2 font-bold min-w-[220px]">İş Kalemi</th>
                  <th className="text-left px-2 py-2 font-bold min-w-[160px]">Planlanan</th>
                  <th className="text-left px-2 py-2 font-bold min-w-[160px]">Gerçekleşen</th>
                  <th className="text-left px-2 py-2 font-bold min-w-[280px]">İlerleme (%5 adım)</th>
                  <th className="text-left px-2 py-2 font-bold w-24">Aksiyon</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">Yükleniyor…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-16 text-slate-500 text-xs">
                    {rows.length === 0
                      ? "Bu şantiyede Work Item bulunmuyor. Önce Work Item Yönetimi'nden kalem oluşturun."
                      : "Filtreye uyan kayıt yok."}
                  </td></tr>
                ) : filtered.map((r, idx) => {
                  const isDirty = dirty[r.id];
                  const isSaving = saving[r.id];
                  const pct = Number(r.progress_percent || 0);
                  const prev = Number(r.prev_progress || 0);
                  const dp = pct - prev;
                  const hkLines = hkMap[r.work_item_id] || [];
                  const wiQty = Number(r.quantity || 0);
                  return (
                    <tr key={r.id} data-testid={`progress-row-${idx}`}
                        className={`hover:bg-slate-50 ${isDirty ? "bg-amber-50/30" : ""}`}>
                      <td className="px-2 py-2 align-top">
                        <div className="flex items-start gap-1.5">
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-900 text-xs">{r.name}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5 truncate max-w-[280px]" title={r.wbs_path}>
                              WBS: {r.wbs_path || "-"}
                            </div>
                            <div className="text-[10px] text-slate-500 truncate max-w-[280px]" title={r.location_label}>
                              Yer: {r.location_label || "-"}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                              {fmt(r.quantity, 2)} {r.unit}
                            </div>
                            {hkLines.length > 0 && (
                              <div className="text-[9px] text-[#556B10] mt-0.5 truncate max-w-[280px]"
                                   title={hkLines.map(l => `${l.subcontractor_name} · ${l.contract_code} · ${fmt(l.unit_price, 2)} ₺/${l.unit}`).join("\n")}>
                                🔧 {hkLines[0].subcontractor_name}
                                {hkLines.length > 1 && ` +${hkLines.length - 1} kontrat`}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <Input type="date"
                                 data-testid={`progress-planned-start-${idx}`}
                                 value={r.planned_start || ""}
                                 onChange={(e) => updateLocal(r.id, { planned_start: e.target.value })}
                                 className="h-7 text-[11px] rounded-none sm:rounded-sm" />
                          <Input type="date"
                                 data-testid={`progress-planned-finish-${idx}`}
                                 value={r.planned_finish || ""}
                                 onChange={(e) => updateLocal(r.id, { planned_finish: e.target.value })}
                                 className="h-7 text-[11px] rounded-none sm:rounded-sm" />
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <Input type="date"
                                 data-testid={`progress-actual-start-${idx}`}
                                 value={r.actual_start || ""}
                                 onChange={(e) => updateLocal(r.id, { actual_start: e.target.value })}
                                 className="h-7 text-[11px] rounded-none sm:rounded-sm" />
                          <Input type="date"
                                 data-testid={`progress-actual-finish-${idx}`}
                                 value={r.actual_finish || ""}
                                 onChange={(e) => updateLocal(r.id, { actual_finish: e.target.value })}
                                 className="h-7 text-[11px] rounded-none sm:rounded-sm" />
                        </div>
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Slider
                              data-testid={`progress-slider-${idx}`}
                              value={[pct]}
                              min={0} max={100} step={5}
                              onValueChange={(v) => updateLocal(r.id, { progress_percent: v[0] })}
                              className="w-full"
                            />
                            <div className="flex items-center justify-between mt-1">
                              <div className="text-[9px] text-slate-400">Önceki: %{fmt(r.prev_progress || 0, 0)}</div>
                              <div className="text-[9px] text-slate-400">Δ {fmt((pct - Number(r.prev_progress || 0)), 0)}%</div>
                            </div>
                          </div>
                          <div className="w-14 text-right">
                            <span className={`text-lg font-bold font-mono ${
                              pct >= 100 ? "text-emerald-600" :
                              pct > 0 ? "text-[#1B4776]" : "text-slate-300"
                            }`}>
                              %{fmt(pct, 0)}
                            </span>
                          </div>
                        </div>
                        {hkLines.length > 0 && dp !== 0 && (
                          <div className="mt-2 space-y-1" data-testid={`progress-hakedis-chip-${idx}`}>
                            {hkLines.map((l, li) => {
                              const dQty = (dp / 100) * wiQty;
                              const dAmt = dQty * (l.unit_price || 0);
                              const isPos = dp > 0;
                              return (
                                <div key={li}
                                     className={`flex items-center gap-2 px-2 py-1 border-l-4 rounded-sm text-[10px] ${
                                       isPos
                                         ? "bg-emerald-50/70 border-emerald-500 text-emerald-900"
                                         : "bg-red-50/70 border-red-500 text-red-900"
                                     }`}
                                     title={`${l.subcontractor_name} — ${l.contract_code} · ${l.line_no} ${l.line_name}`}>
                                  <span className="inline-block px-1 py-0.5 bg-[#1B4776] text-white text-[9px] uppercase tracking-widest font-bold rounded-sm">
                                    Hakediş
                                  </span>
                                  <span className="font-semibold truncate max-w-[160px]">
                                    {l.subcontractor_name}
                                  </span>
                                  <span className="text-[9px] text-slate-500 truncate max-w-[130px]">
                                    · {l.contract_code}
                                  </span>
                                  <span className="ml-auto font-mono font-bold">
                                    Δ{fmt(dQty, 1)} {l.unit}
                                  </span>
                                  <span className="font-mono font-bold text-[#1B4776]">
                                    · {isPos ? "+" : ""}{fmt(dAmt, 0)} ₺
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <div className="flex flex-col gap-1">
                          <Button size="sm"
                                  data-testid={`progress-save-${idx}`}
                                  disabled={!isDirty || isSaving}
                                  onClick={() => save(r)}
                                  className="h-7 text-[10px] rounded-none sm:rounded-sm uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
                            <FloppyDisk size={11} className="mr-1" />
                            {isSaving ? "..." : "Kaydet"}
                          </Button>
                          <Button size="sm" variant="ghost"
                                  data-testid={`progress-logs-${idx}`}
                                  onClick={() => openLogs(r)}
                                  className="h-7 text-[10px] rounded-none sm:rounded-sm uppercase tracking-widest">
                            <Clock size={11} className="mr-1" /> Loglar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add Month Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-widest text-sm">Yeni Ay Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Yıl-Ay</label>
              <Input type="month"
                     data-testid="progress-new-month-input"
                     value={newYm}
                     onChange={(e) => setNewYm(e.target.value)}
                     className="h-9 rounded-none sm:rounded-sm mt-1" />
            </div>
            <div className="text-[11px] text-slate-500 bg-slate-50 p-2 border-l-2 border-[#95C11F]">
              Bu adımda önceki ayın <b>tamamlanmamış</b> tüm Work Item'ları yeni aya taşınır.
              Önceki ay ilerleme yüzdesi başlangıç değeri olur; %100 olanlar atlanır.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}
                    className="rounded-none sm:rounded-sm uppercase text-xs tracking-widest">İptal</Button>
            <Button onClick={addMonth}
                    data-testid="progress-add-month-confirm"
                    className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea01a] text-white uppercase text-xs tracking-widest">
              <Plus size={13} className="mr-1" /> Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={!!logsFor} onOpenChange={(o) => !o && setLogsFor(null)}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-widest text-sm">
              İlerleme Günlüğü — {logsFor?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="progress-logs-list">
            {logs.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-500">Kayıt yok.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-bold">Tarih</th>
                    <th className="text-left px-2 py-1.5 font-bold">Ay</th>
                    <th className="text-right px-2 py-1.5 font-bold">Öncesi</th>
                    <th className="text-right px-2 py-1.5 font-bold">Sonrası</th>
                    <th className="text-right px-2 py-1.5 font-bold">Δ</th>
                    <th className="text-left px-2 py-1.5 font-bold">Kullanıcı</th>
                    <th className="text-left px-2 py-1.5 font-bold">Not</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logs.map((l) => (
                    <tr key={l.id}>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600">
                        {new Date(l.at).toLocaleString("tr-TR")}
                      </td>
                      <td className="px-2 py-1.5">{l.year_month}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-slate-500">%{fmt(l.from_pct, 0)}</td>
                      <td className="text-right px-2 py-1.5 font-mono font-bold text-[#1B4776]">%{fmt(l.to_pct, 0)}</td>
                      <td className={`text-right px-2 py-1.5 font-mono font-bold ${
                        l.delta > 0 ? "text-emerald-600" : "text-red-600"
                      }`}>
                        {l.delta > 0 ? "+" : ""}{fmt(l.delta, 0)}
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">{l.by_user_name || "-"}</td>
                      <td className="px-2 py-1.5 text-slate-500 max-w-[200px] truncate" title={l.note}>{l.note || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogsFor(null)}
                    className="rounded-none sm:rounded-sm uppercase text-xs tracking-widest">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
